import {
  CLIENT_STATUS_OPTIONS,
  CLIENT_TYPE_OPTIONS,
  ClientsQueryKeys,
} from "@/api/clients/clients.queries";
import type { Client, ClientApiResponse } from "@/api/clients/clients.queries.types";
import type { TUpcomingVisit } from "@/api/clients/OverviewTab/overviewTab.queries.types";
import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import AssetsModal from "@/components/features/ClientOnboarding/AssetsInfo/AssetsModal/AssetsModal";
import BrandsSearchModal from "@/components/features/ClientOnboarding/Brands/BrandsSearchModal/BrandsSearchModal";
import ContactsModal from "@/components/features/ClientOnboarding/Contacts/ContactsModal/ContactsModal";
import AddOrEditLocationsModal from "@/components/features/ClientOnboarding/LocationInfo/AddOrEditLocations/AddOrEditLocationsModal";
import Loader from "@/components/ui/Loader/Loader";
import Table from "@/components/ui/Table/Table";
import TableActionsMenu from "@/components/ui/TableActionsMenu/TableActionsMenu";
import type { TColumn } from "@/components/ui/TableCard/TableCard";
import TableCellWithAvatar from "@/components/ui/TableCellWithAvatar/TableCellWithAvatar";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { useActiveSession } from "@/hooks/useActiveSession";
import { useInsideEntity, usePlatformView } from "@/hooks/usePlatformView";
import {
  useClientListWithFilters,
  useClientOverviewDetails,
} from "@/hooks/clients/useClientListQueries";
import { useTeamCounts } from "@/hooks/team-assignments/useTeamAssignments";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { tableSearchSchema, useTableUrlPersistence } from "@/hooks/useTableUrlPersistence";
import { useClientOnboardingStore } from "@/store/clientOnboardingStore";
import { useCoverageStore } from "@/store/coverageStore/coverageStore";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getVisitTypeBadgeColors } from "@/utils/badgeConfig";
import { buildTableQueryOptions } from "@/utils/buildTableQueryOptions";
import { formatDateTime } from "@/utils/dateFormatter";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { isAppLevelUser } from "@/utils/isPlatformView";
import { getStatusBadgeProps } from "@/utils/statusBadge";
import {
  Box,
  Drawer,
  Flex,
  Group,
  Popover,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  Alert,
  Badge,
  Button,
  DropdownMenu,
  DropdownRow,
  SplitButton,
} from "@onengine/component-library";
import {
  IconAddressBook,
  IconBrowser,
  IconExternalLink,
  IconEye,
  IconLibrary,
  IconMapPin,
  IconPackage,
  IconPlus,
  IconRadar,
  IconReceipt2,
  IconUpload,
  IconUsersGroup,
} from "@tabler/icons-react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import BulkImportModal from "@/components/ui/BulkImport/BulkImportModal";
import { clientImportDescriptor } from "@/components/features/Clients/BulkUpload/clientImportDescriptor";
import {
  FINANCE_COLUMN_KEYS,
  filterFinanceColumns,
  resolveCanViewFinance,
} from "@/utils/clientFinanceGating";
import { z } from "zod";
import styles from "./clients.module.scss";

const clientsSearchSchema = tableSearchSchema.extend({
  quickViewClientId: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_authenticated/clients/")({
  component: Clients,
  validateSearch: clientsSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

export type ClientColumnType = {
  id: string;
  name: string;
  description?: string;
  status: "Active" | "Inactive" | "Prospect";
  clientType: "Primary" | "Secondary";
  locations: number;
  brands: number;
  activeWorkOrders: number;
  clientSuccessManager: string;
  teamLead: string;
  entityLead: string;
  paymentMethod: string;
  paymentTerms: string;
};

type NextUpcomingVisit = {
  id: string;
  workOrderId: string;
  date: string;
  time: string;
  workOrder: string;
  visitType: string;
  locationName: string;
};

// SERV-2234: finance columns (clientSuccessManager, paymentMethod, paymentTerms)
// are filtered out of the initial state and only appended once the `/client/list`
// response's `canViewFinance` flag confirms access (see the effect in Clients()).
const ENTITY_COLUMN_ID = "entityName";

const ALL_CLIENT_TABLE_COLUMNS: TColumn[] = [
  {
    id: "name",
    label: "Client Name",
    visible: true,
    sortable: true,
    searchParams: {
      type: "clients",
      fields: ["businessName"],
      limit: 10,
      key: "businessName",
      filterByKey: "businessName",
    },
  },
  // SERV-3225: second so it stays on screen, as on the Work Orders list
  // (SERV-2494 [3.1]). Left out of the initial column state inside one entity,
  // where every row would repeat the same value. Unlike Work Orders this is a
  // one-shot: the picker mutates tableColumns afterwards, so recomputing on the
  // session would discard the user's edits. Not sortable and no filter, matching
  // the sibling lists; the filter is SERV-3293.
  {
    id: ENTITY_COLUMN_ID,
    label: "Entity",
    visible: true,
    sortable: false,
  },
  {
    id: "status",
    label: "Status",
    visible: true,
    sortable: true,
    searchParams: {
      type: "clients",
      fields: ["value"],
      limit: 10,
      key: "status",
      filterByKey: "status",
    },
  },
  {
    id: "clientType",
    label: "Client Type",
    visible: true,
    sortable: true,
    searchParams: {
      type: "clients",
      fields: ["value"],
      limit: 10,
      key: "clientType",
      filterByKey: "clientType",
    },
  },
  {
    id: "locations",
    label: "# of Locations",
    visible: true,
    sortable: true,
  },
  {
    id: "brands",
    label: "# of Brands",
    visible: true,
    sortable: true,
  },
  {
    id: "activeWorkOrders",
    label: "Active Work Orders",
    visible: true,
    sortable: true,
  },
  {
    id: "clientSuccessManager",
    label: "Client Success M",
    visible: true,
    sortable: true,
    searchParams: {
      type: "clients",
      fields: ["clientSuccessManager"],
      limit: 10,
      key: "clientSuccessManager",
      filterByKey: "clientSuccessManager",
    },
  },
  {
    id: "paymentMethod",
    label: "Payment Method",
    visible: true,
    sortable: true,
  },
  {
    id: "paymentTerms",
    label: "Payment Terms",
    visible: true,
    sortable: true,
    searchParams: {
      type: "clients",
      fields: ["label"],
      limit: 10,
      key: "paymentTerms",
      filterByKey: "paymentTerms",
    },
  },
];

function Clients() {
  const navigate = useNavigate();
  const searchParams = useSearch({ from: "/_authenticated/clients/" });
  useBreadcrumb(
    [
      {
        title: "Clients",
        href: "/clients",
        icon: getRouteIcon(MODULE_KEYS_MAP.clients),
      },
    ],
    { listView: "List View" },
  );
  const [clientDetailsDrawerOpened, clientDetailsDrawerHandlers] = useDisclosure(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

  useEffect(() => {
    if (!searchParams.quickViewClientId) return;
    setSelectedClientId(searchParams.quickViewClientId);
    clientDetailsDrawerHandlers.open();
    navigate({
      to: "/clients",
      search: (prev) => {
        const next = { ...prev };
        delete next.quickViewClientId;
        return next;
      },
      replace: true,
    });
  }, [searchParams.quickViewClientId]);
  const [locationModalOpened, locationModalHandlers] = useDisclosure(false);
  const [brandsModalOpened, brandsModalHandlers] = useDisclosure(false);
  const [assetsModalOpened, assetsModalHandlers] = useDisclosure(false);
  const [contactsModalOpened, contactsModalHandlers] = useDisclosure(false);
  const [modalClientId, setModalClientId] = useState<string | null>(null);
  const [isNewClientMenuOpen, setIsNewClientMenuOpen] = useState(false);
  const [createAction, setCreateAction] = useState<"new-client" | "bulk-import">("new-client");
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  const { hasAccess } = usePermissions();
  const {
    create: canCreateClients,
    edit: canEditClients,
    view: canViewClients,
  } = hasAccess({
    module: MODULE_KEYS_MAP.clients,
  });

  const { view: canViewTeamAssignments } = hasAccess({
    module: MODULE_KEYS_MAP.clientAlignment,
  });
  const { counts: teamCounts } = useTeamCounts({ enabled: canViewTeamAssignments });
  const teamAlertMessage = [
    teamCounts.unassigned > 0
      ? `${teamCounts.unassigned} ${teamCounts.unassigned === 1 ? "has" : "have"} no team assigned`
      : null,
    teamCounts.partial > 0
      ? `${teamCounts.partial} ${teamCounts.partial === 1 ? "has" : "have"} a partial team`
      : null,
  ]
    .filter(Boolean)
    .join(", and ")
    .concat(".");
  // SERV-3225: the backend widens /client/list to every entity when the
  // session is not an entity login (client/controller.ts getAllClients), so the
  // Entity column follows the same predicate as the other top-level lists.
  const isPlatformView = usePlatformView();
  // A client is created inside an entity. At ServEngine level there is no
  // entity to own it, so the create entry points stay hidden there, the same
  // rule the home page applies to its New Client action. SERV-3232: an
  // app-level user who picked one of her entities is inside it, so they open
  // for her as at the subdomain (the MAIN_APP_HIDDEN_ROUTES rule); her merged
  // view and a super admin keep them hidden.
  const { isMainAppLogin, isAppLevel, allEntities, userType } = useActiveSession();
  const insideEntity = useInsideEntity();
  const appLevelUser = isAppLevelUser({ isAppLevel, allEntities: allEntities ?? false, userType });
  const canCreateHere = canCreateClients && (!isMainAppLogin || (appLevelUser && insideEntity));

  const [tableColumns, setTableColumns] = useState<TColumn[]>(() =>
    filterFinanceColumns(
      isPlatformView
        ? ALL_CLIENT_TABLE_COLUMNS
        : ALL_CLIENT_TABLE_COLUMNS.filter((col) => col.id !== ENTITY_COLUMN_ID),
      false,
      (col) => col.id,
    ),
  );
  const financeColumnsAppliedRef = useRef(false);

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    setSearchValue,
    sorting,
    setSorting,
    debouncedSearch,
    filters,
    viewName,
    filtersKey,
    applyFilters,
    resetFilters,
    isPrefsLoading,
  } = useTableUrlPersistence({
    viewName: "clientList",
    filterKeyToLabel: CLIENTS_FILTER_KEY_TO_LABEL,
    searchParams,
    navigate,
    tableColumns,
  });

  const queryOptions = useMemo(
    () =>
      buildTableQueryOptions({
        page,
        pageSize,
        search: debouncedSearch,
        sorting,
        filters,
        // Label-to-key lookup, not a permission check: the backend already
        // strips finance filter keys server-side, so resolving against the
        // full column list (rather than the gated one) avoids a malformed
        // key going out while the finance columns aren't mounted yet.
        tableColumns: ALL_CLIENT_TABLE_COLUMNS,
        getSortByField,
      }),
    [page, pageSize, debouncedSearch, sorting, filtersKey],
  );

  const {
    data: clientsData,
    isFetching,
    isLoading,
    isPlaceholderData,
  } = useClientListWithFilters(queryOptions);
  const { data: clientOverviewData, isFetching: overviewFetching } =
    useClientOverviewDetails(selectedClientId);

  const canViewFinance = resolveCanViewFinance(clientsData);

  // One-shot: only ever grants the finance columns, never revokes them again
  // within the same session. If access were revoked mid-session (a rare
  // case), the columns would stay visible until the next full page load.
  useEffect(() => {
    if (financeColumnsAppliedRef.current || !canViewFinance) return;
    financeColumnsAppliedRef.current = true;
    setTableColumns((prev) => [
      ...prev,
      ...ALL_CLIENT_TABLE_COLUMNS.filter((col) => FINANCE_COLUMN_KEYS.has(col.id)),
    ]);
  }, [canViewFinance]);

  const clientColumns = useMemo<ColumnDef<Client, unknown>[]>(
    () =>
      filterFinanceColumns(
        [
          {
            accessorKey: "name",
            header: "Client Name",
            cell: ({ getValue }) => (
              <Text size="sm" fw={"var(--font-weight-medium)"}>
                {getValue() as string}
              </Text>
            ),
          },
          {
            accessorKey: ENTITY_COLUMN_ID,
            header: "Entity",
            cell: ({ getValue }) => {
              const entityName = getValue() as string | null;
              return <Text size="sm">{entityName || "—"}</Text>;
            },
            enableSorting: false,
          },
          {
            accessorKey: "status",
            header: "Status",
            cell: ({ getValue }) => {
              const status = getValue() as string;
              const clientStatusProps = getStatusBadgeProps(status);
              return (
                <Badge
                  size="medium"
                  color={clientStatusProps?.color}
                  variant="plain"
                  iconLeft={clientStatusProps.iconLeft}
                >
                  {clientStatusProps?.text}
                </Badge>
              );
            },
          },
          {
            accessorKey: "clientType",
            header: "Client Type",
            cell: (props) => {
              const clientType = props.getValue() as string;
              // Map to the expected format for badge colors
              const mappedType = clientType === "Primary" ? "Primary" : "Secondary";
              const clientTypeBadgeColors = getClientTypeBadgeColors(
                mappedType as ClientColumnType["clientType"],
              );
              return (
                <Badge size="medium" color={clientTypeBadgeColors.color} variant="lowContrast">
                  {clientType}
                </Badge>
              );
            },
          },
          {
            accessorKey: "locations",
            header: "# of Locations",
            cell: ({ getValue }) => (
              <Text size="sm" ta="center">
                {getValue() as number}
              </Text>
            ),
          },
          {
            accessorKey: "brands",
            header: "# of Brands",
            cell: ({ getValue }) => (
              <Text size="sm" ta="center">
                {getValue() as number}
              </Text>
            ),
          },
          {
            accessorKey: "activeWorkOrders",
            header: "Active Work Orders",
            cell: ({ getValue }) => (
              <Text size="sm" ta="center">
                {getValue() as number}
              </Text>
            ),
          },
          {
            accessorKey: "clientSuccessManager",
            header: "Client Success M",
            cell: ({ getValue }) => {
              const name = getValue() as string | null;
              return name ? <TableCellWithAvatar name={name} /> : <Text size="sm">—</Text>;
            },
          },
          {
            accessorKey: "paymentMethod",
            header: "Payment Method",
            cell: ({ getValue }) => {
              const name = getValue() as string | null;
              return name ? (
                <Text size="sm" ta={"right"}>
                  {getValue() as string}
                </Text>
              ) : (
                <Text size="sm">—</Text>
              );
            },
          },
          {
            accessorKey: "paymentTerms",
            header: "Payment Terms",
            cell: ({ getValue }) => {
              const name = getValue() as string | null;
              return name ? (
                <Text size="sm" ta={"right"}>
                  {getValue() as string}
                </Text>
              ) : (
                <Text size="sm">—</Text>
              );
            },
          },
          {
            accessorKey: "actions",
            header: "",
            cell: ({ row }) => (
              <TableActionsMenu
                options={[
                  ...(canViewClients
                    ? [
                        {
                          btnText: "View Details",
                          onClickHandler: () => {
                            navigate({
                              to: "/clients/$id",
                              params: { id: row.original.id },
                              search: { tab: "overview" },
                            });
                          },
                          iconLeft: <IconEye size={20} />,
                        },
                        {
                          btnText: "View Rates",
                          onClickHandler: () => {
                            navigate({
                              to: "/clients/$id",
                              params: { id: row.original.id },
                              search: { tab: "rates" },
                            });
                          },
                          iconLeft: <IconReceipt2 size={20} />,
                        },
                        {
                          btnText: "View Coverage Area",
                          onClickHandler: () => {
                            navigate({
                              to: "/clients/$id",
                              params: { id: row.original.id },
                              search: { tab: "coverage-area" },
                            });
                          },
                          iconLeft: <IconRadar size={20} />,
                        },
                      ]
                    : []),
                  ...(canEditClients && canViewClients ? [{ type: "divider" as const }] : []),
                  ...(canEditClients
                    ? [
                        {
                          btnText: "Add Location",
                          onClickHandler: () => {
                            setModalClientId(row.original.id);
                            locationModalHandlers.open();
                          },
                          iconLeft: <IconMapPin size={20} />,
                        },
                        {
                          btnText: "Add Brand",
                          onClickHandler: () => {
                            setModalClientId(row.original.id);
                            brandsModalHandlers.open();
                          },
                          iconLeft: <IconLibrary size={20} />,
                        },
                        {
                          btnText: "Add Asset",
                          onClickHandler: () => {
                            setModalClientId(row.original.id);
                            setClientId(row.original.id);
                            assetsModalHandlers.open();
                          },
                          iconLeft: <IconPackage size={20} />,
                        },
                        {
                          btnText: "Add Contact",
                          onClickHandler: () => {
                            setModalClientId(row.original.id);
                            contactsModalHandlers.open();
                          },
                          iconLeft: <IconAddressBook size={20} />,
                        },
                      ]
                    : []),
                ]}
                isMenuDisabled={false}
                menuId={1}
              />
            ),
            enableSorting: false,
            enableHiding: false,
          },
        ],
        canViewFinance,
        (col) => col.accessorKey as string | undefined,
      ),
    [canViewFinance],
  );

  // Show loading overlay on:
  // 1. Initial load (isLoading)
  // 2. Pagination/filter changes when showing placeholder data from previous query
  const showLoadingOverlay = isLoading || (isFetching && isPlaceholderData) || isPrefsLoading;

  const { setClientId } = useClientOnboardingStore();
  const { resetCoverageStore } = useCoverageStore();

  const metricsSummary = useMemo(
    () =>
      clientOverviewData
        ? Object.entries(mapConfig).map(([label, config]) => ({
            label,
            value: String(clientOverviewData?.metrics[config.metricKey] ?? 0),
            tab: config.tab,
          }))
        : [],
    [clientOverviewData],
  );

  const workOrdersSummary = useMemo(() => {
    if (!clientOverviewData?.workOrders) {
      return [];
    }

    const {
      totalWorkOrders,
      activeWorkOrdersCount,
      awaitingPaymentCount,
      onTimeCompletionPercentage,
    } = clientOverviewData?.workOrders ?? {};

    const onTimeDisplay =
      onTimeCompletionPercentage !== null && onTimeCompletionPercentage !== undefined
        ? String(onTimeCompletionPercentage)
        : "0%";

    return [
      { label: "Total Work Orders", value: String(totalWorkOrders ?? 0) },
      { label: "Active Work Orders", value: String(activeWorkOrdersCount ?? 0) },
      { label: "Awaiting Payment", value: String(awaitingPaymentCount ?? 0) },
      { label: "On Time Completion", value: onTimeDisplay },
    ];
  }, [clientOverviewData]);

  const paymentsAndInvoicesSummary = useMemo(
    () =>
      clientOverviewData?.paymentsInvoices
        ? [
            {
              title: String(clientOverviewData?.paymentsInvoices?.totalUnpaidInvoices ?? 0),
              subtitle: "Total Unpaid Invoices",
            },
            {
              title: clientOverviewData?.paymentsInvoices?.totalUnpaidAmount ?? "0",
              subtitle: "Total Unpaid Amount",
            },
            {
              title: String(clientOverviewData?.paymentsInvoices?.dueInvoices ?? 0),
              subtitle: "Due Invoices",
            },
            {
              title: clientOverviewData?.paymentsInvoices?.dueAmount ?? "0",
              subtitle: "Due Amount",
            },
            {
              title: String(clientOverviewData?.paymentsInvoices?.overdueInvoices ?? 0),
              subtitle: "Overdue Invoices",
            },
            {
              title: clientOverviewData?.paymentsInvoices?.overdueAmount ?? "0",
              subtitle: "Overdue Amount",
            },
          ]
        : [],
    [clientOverviewData],
  );

  const nextUpcomingVisits: NextUpcomingVisit[] = useMemo(() => {
    if (!clientOverviewData?.upcomingVisits?.length) {
      return [];
    }

    return clientOverviewData?.upcomingVisits?.map((visit: TUpcomingVisit) => ({
      id: visit.id,
      workOrderId: visit.workOrderId,
      date: formatDateTime(visit.scheduledTime, false, true),
      time: formatDateTime(visit.scheduledTime, true, false),
      workOrder: visit.workOrderNumber,
      visitType: visit.visitType,
      locationName: visit.locationName,
    }));
  }, [clientOverviewData]);

  const tableData = useMemo(() => {
    if (!clientsData?.data?.data) {
      return [];
    }
    return clientsData?.data?.data?.map(mapClientApiToClient);
  }, [clientsData]);

  const drawerNextVisitsColumns = useMemo<ColumnDef<NextUpcomingVisit, unknown>[]>(
    () => [
      {
        accessorKey: "date",
        header: "Visit Time",
        cell: (props) => (
          <Stack gap={"var(--spacing-2x-small)"}>
            <Text size="sm" fw={"var(--font-weight-regular)"}>
              {props.row.original.date}
            </Text>
            <Text size="sm" fw={"var(--font-weight-regular)"}>
              {props.row.original.time}
            </Text>
          </Stack>
        ),
      },
      {
        accessorKey: "workOrder",
        header: "Work Order #",
        cell: (props) => {
          return (
            <Text size="sm" fw={"var(--font-weight-regular)"}>
              {props.getValue() as string}
            </Text>
          );
        },
      },
      {
        accessorKey: "visitType",
        header: "Visit Type",
        cell: (props) => {
          const visitType = props.getValue() as string;
          return (
            <Badge size="medium" {...getVisitTypeBadgeColors(visitType)}>
              {visitType}
            </Badge>
          );
        },
      },
      {
        accessorKey: "locationName",
        header: "Location Name",
        cell: (props) => <Text size="sm">{props.getValue() as string}</Text>,
      },
      {
        accessorKey: "actions",
        header: "",
        cell: ({ row }) => (
          <TableActionsMenu
            options={[
              {
                btnText: "Edit",
                onClickHandler: () =>
                  navigate({
                    to: "/work-orders/$workOrderId/$visitId/edit",
                    params: {
                      workOrderId: row.original.workOrderId,
                      visitId: row.original.id,
                    },
                    search: selectedClientId
                      ? { returnTo: "clients" as const, returnId: selectedClientId }
                      : {},
                  }),
              },
            ]}
            isMenuDisabled={false}
            menuId={1}
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
    ],
    [navigate, selectedClientId],
  );

  const filterData = useMemo(() => {
    const result: Record<string, string[]> = {};

    tableColumns.forEach((col) => {
      if (col.id === "status") {
        result[col.id] = CLIENT_STATUS_OPTIONS.map((opt) => opt.label);
      } else if (col.id === "clientType") {
        result[col.id] = CLIENT_TYPE_OPTIONS.map((opt) => opt.label);
      } else if (col.searchParams) {
        const uniqueValues = new Set<string>();
        tableData?.forEach((client: any) => {
          const value = client[col.id as keyof Client];
          if (value !== undefined && value !== null) {
            uniqueValues.add(String(value));
          }
        });
        result[col.id] = Array.from(uniqueValues).sort();
      }
    });

    return result;
  }, [tableColumns, tableData]);

  const handleSortChange = (columnId: string | null, direction: "asc" | "desc") => {
    if (columnId) {
      setSorting([{ id: columnId, desc: direction === "desc" }]);
    } else {
      setSorting([]);
    }
  };

  return (
    <>
      <Flex direction="column" className={styles.container}>
        <Flex direction="column" gap="var(--spacing-large)">
          <Flex justify="space-between" align="center">
            <Title order={3} className={styles.title}>
              Clients
            </Title>
            <Group gap="sm">
              {canViewTeamAssignments && (
                <Button
                  variant="secondary"
                  iconLeft={<IconUsersGroup size={16} />}
                  onClick={() => navigate({ to: "/clients/team-assignments" })}
                >
                  Team Assignments
                </Button>
              )}
              {canCreateHere && (
                <Popover
                  opened={isNewClientMenuOpen}
                  onChange={setIsNewClientMenuOpen}
                  position="bottom-end"
                  offset={6}
                  withinPortal
                >
                  <Popover.Target>
                    <div>
                      <SplitButton
                        variant="primary"
                        size="small"
                        label={createAction === "new-client" ? "New Client" : "Bulk Import"}
                        iconLeft={
                          createAction === "new-client" ? (
                            <IconPlus size={16} />
                          ) : (
                            <IconUpload size={16} />
                          )
                        }
                        onMainClick={() => {
                          if (createAction === "bulk-import") {
                            setBulkImportOpen(true);
                            return;
                          }
                          setClientId("");
                          resetCoverageStore();
                          localStorage.removeItem("client-onboarding-storage");
                          navigate({
                            to: "/clients/add-new-client/$stepId",
                            params: { stepId: "basic-info" },
                          });
                        }}
                        onSplitClick={() => setIsNewClientMenuOpen((open) => !open)}
                        splitButtonProps={{ "aria-label": "Choose create action" }}
                      />
                    </div>
                  </Popover.Target>
                  <Popover.Dropdown p={0}>
                    <DropdownMenu>
                      {createAction !== "new-client" && (
                        <DropdownRow
                          leftSection={<IconPlus size={16} />}
                          title="New Client"
                          onClick={() => {
                            setCreateAction("new-client");
                            setIsNewClientMenuOpen(false);
                          }}
                        />
                      )}
                      {createAction !== "bulk-import" && (
                        <DropdownRow
                          leftSection={<IconUpload size={16} />}
                          title="Bulk Import"
                          onClick={() => {
                            setCreateAction("bulk-import");
                            setIsNewClientMenuOpen(false);
                          }}
                        />
                      )}
                    </DropdownMenu>
                  </Popover.Dropdown>
                </Popover>
              )}
            </Group>
          </Flex>
          {canViewTeamAssignments && teamCounts.needsUpdate > 0 && (
            <div className={styles.teamAlert}>
              <Alert
                presentation="inline"
                type="warning"
                variant="strong"
                closable={false}
                title={`${teamCounts.needsUpdate} ${teamCounts.needsUpdate === 1 ? "client needs" : "clients need"} a team update`}
                message={teamAlertMessage}
                actions={[
                  {
                    label: "Review teams",
                    onClick: () =>
                      navigate({
                        to: "/clients/team-assignments",
                        search: { tab: teamCounts.unassigned > 0 ? "unassigned" : "partial" },
                      }),
                  },
                ]}
              />
            </div>
          )}
          {canCreateHere && (
            <BulkImportModal
              descriptor={clientImportDescriptor}
              ctx={undefined}
              opened={bulkImportOpen}
              onClose={() => setBulkImportOpen(false)}
            />
          )}
          <Table
            data={tableData}
            columns={clientColumns}
            sorting={sorting}
            onSortingChange={setSorting}
            manualPagination={true}
            pageCount={Math.ceil((clientsData?.data?.pagination?.total || 0) / pageSize)}
            pageIndex={page - 1}
            totalRecords={clientsData?.data?.pagination?.total || 0}
            onPageChange={(newPageIndex) => setPage(newPageIndex + 1)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
            pageSize={pageSize}
            onRowClick={(row) => {
              setSelectedClientId(row.id);
              clientDetailsDrawerHandlers.open();
            }}
            columnPinning={{
              left: [],
              right: ["actions"],
            }}
            showTableCard={true}
            isLoading={showLoadingOverlay}
            tableCardProps={{
              columns: tableColumns,
              onColumnsChange: setTableColumns,
              onSearchChange: setSearchValue,
              onSortChange: handleSortChange,
              filterData,
              filterStoreType: "clients",
              isLoading: isFetching,
              initialSearchValue: searchParams.search,
              viewName,
              onApplyFilters: applyFilters,
              onResetFilters: resetFilters,
            }}
          />
        </Flex>
      </Flex>

      {/* Client Details Drawer */}
      {/* This component is only for UI structure, we can reuse things once api is implemented */}
      <Drawer
        position="right"
        opened={clientDetailsDrawerOpened}
        onClose={clientDetailsDrawerHandlers.close}
        size={752} // width of the drawer taken from figma
        radius={"var(--radius-large) 0 0 var(--radius-large)"}
        title={
          <>
            {clientOverviewData && (
              <Flex justify="space-between" align="center" w={"100%"}>
                <Title order={3} className={styles.drawerTitle}>
                  {clientOverviewData.businessName}
                </Title>
                <Button
                  variant="secondary"
                  iconRight={<IconExternalLink size={16} />}
                  className={styles.drawerPrimaryAction}
                  onClick={() => {
                    if (selectedClientId) {
                      navigate({
                        to: "/clients/$id",
                        params: { id: selectedClientId },
                      });
                      clientDetailsDrawerHandlers.close();
                    }
                  }}
                >
                  View Details
                </Button>
              </Flex>
            )}
          </>
        }
        classNames={{
          body: styles.drawerBody,
          header: styles.drawerHeader,
          title: styles.drawerTitle,
        }}
      >
        {overviewFetching ? (
          <Loader size="medium" />
        ) : (
          <>
            {clientOverviewData && (
              <Flex direction="column" gap="var(--spacing-3x-large)">
                <Stack gap="var(--spacing-medium)">
                  <Group gap="var(--spacing-medium)">
                    {clientOverviewData.clientType?.label && (
                      <Badge
                        key={"client-type-badge"}
                        size="small"
                        color="indigo"
                        variant="highContrast"
                      >
                        {clientOverviewData?.clientType?.label}
                      </Badge>
                    )}
                    {clientOverviewData?.clientIndustry?.label && (
                      <Badge
                        key={"client-industry-badge"}
                        size="small"
                        color="neutral"
                        variant="outline"
                      >
                        {clientOverviewData?.clientIndustry?.label}
                      </Badge>
                    )}
                  </Group>
                  <Group gap="var(--spacing-medium)" align="center">
                    {clientOverviewData?.hqAddress?.fullAddress && (
                      <UnstyledButton className={styles.drawerAddressPill}>
                        <IconMapPin size={16} />
                        <Text className={styles.drawerAddressPillText}>
                          {clientOverviewData?.hqAddress?.fullAddress}
                        </Text>
                        <IconExternalLink size={16} />
                      </UnstyledButton>
                    )}
                    {clientOverviewData?.website && (
                      <UnstyledButton className={styles.drawerAddressPill}>
                        <IconBrowser size={16} />
                        <Text className={styles.drawerAddressPillText}>
                          {clientOverviewData?.website}
                        </Text>
                        <IconExternalLink size={16} />
                      </UnstyledButton>
                    )}
                  </Group>
                </Stack>

                <Stack gap="var(--spacing-small)">
                  <Text className={styles.drawerSectionTitle}>Metrics</Text>
                  <Box className={styles.drawerSectionGrid}>
                    {metricsSummary.map((metric) => (
                      <Box key={metric.label} className={styles.drawerMetricItem}>
                        <Flex align="center" justify="space-between">
                          <Text className={styles.drawerGridItemTitle}>{metric.value}</Text>
                          <UnstyledButton
                            className={styles.drawerMetricItemExternalLink}
                            onClick={() => {
                              navigate({
                                to: "/clients/$id",
                                params: { id: selectedClientId || "" },
                                search: { tab: metric.tab },
                              });
                            }}
                          >
                            {<IconExternalLink size={16} />}
                          </UnstyledButton>
                        </Flex>
                        <Text className={styles.drawerGridItemSubtitle}>{metric.label}</Text>
                      </Box>
                    ))}
                  </Box>
                </Stack>

                <Stack gap="var(--spacing-small)">
                  <Text className={styles.drawerSectionTitle}>Work Orders</Text>
                  <Box className={styles.drawerSectionGrid}>
                    {workOrdersSummary.map(({ label, value }) => (
                      <Box
                        key={label}
                        className={styles.drawerWorkOrderItem}
                        style={{ justifyContent: "center" }}
                      >
                        {value === "0" ? (
                          <Text className={styles.drawerGridItemSubtitle}>
                            No {label.toLowerCase()}
                          </Text>
                        ) : (
                          <>
                            <Text className={styles.drawerGridItemTitle}>{value}</Text>
                            <Text className={styles.drawerGridItemSubtitle}>{label}</Text>
                          </>
                        )}
                      </Box>
                    ))}
                  </Box>
                  <Group gap={"var(--spacing-medium)"}>
                    <Text className={styles.drawerSectionTitle}>Next Upcoming Visits</Text>
                    <Button
                      variant="tertiary"
                      fz={"var(--text-font-size-small)"}
                      fw={"var(--font-weight-medium)"}
                      onClick={() => {
                        navigate({
                          to: "/work-orders",
                          search: { tab: "upcoming" },
                        });
                      }}
                    >
                      View all
                    </Button>
                  </Group>
                  {!overviewFetching && nextUpcomingVisits.length === 0 ? (
                    <Box className={styles.drawerEmptyMessageContainer}>
                      <Text className={styles.drawerEmptyMessage}>
                        No upcoming visits scheduled
                      </Text>
                    </Box>
                  ) : (
                    <Box className={styles.drawerNextUpcomingVisitsTable}>
                      <Table
                        data={nextUpcomingVisits}
                        columns={drawerNextVisitsColumns}
                        showTableCard={false}
                        enablePagination={false}
                        isLoading={overviewFetching}
                      />
                    </Box>
                  )}
                </Stack>

                {paymentsAndInvoicesSummary.length > 0 && (
                  <Stack gap="var(--spacing-small)">
                    <Text className={styles.drawerSectionTitle}>Payments & Invoices</Text>
                    <Box className={styles.drawerUpcomingVisitsGrid}>
                      {paymentsAndInvoicesSummary.map(({ title, subtitle }) => (
                        <Box key={subtitle} className={styles.drawerUpcomingVisitsItem}>
                          <Text className={styles.drawerGridItemTitle}>{title}</Text>
                          <Text className={styles.drawerGridItemSubtitle}>{subtitle}</Text>
                        </Box>
                      ))}
                    </Box>
                  </Stack>
                )}
              </Flex>
            )}
          </>
        )}
      </Drawer>

      {/* Modals */}
      <AddOrEditLocationsModal
        openModal={locationModalOpened}
        setOpenModal={locationModalHandlers.close}
        editingLocationId={null}
        clientId={modalClientId}
        //Intentional prefix invalidation: since this query key is only used for table data, we can safely invalidate the entire list
        queryKeysToInvalidate={[ClientsQueryKeys.list]}
      />

      <BrandsSearchModal
        openModal={brandsModalOpened}
        setOpenModal={brandsModalHandlers.close}
        clientId={modalClientId}
        //Intentional prefix invalidation: since this query key is only used for table data, we can safely invalidate the entire list
        queryKeysToInvalidate={[ClientsQueryKeys.list]}
      />

      <AssetsModal
        openModal={assetsModalOpened}
        setOpenModal={assetsModalHandlers.close}
        editingAsset={null}
      />

      <ContactsModal
        openModal={contactsModalOpened}
        setOpenModal={(open) => {
          const next = typeof open === "function" ? open(contactsModalOpened) : open;
          if (next) contactsModalHandlers.open();
          else contactsModalHandlers.close();
        }}
        setAssets={() => {}}
        editingAsset={null}
        clientId={modalClientId ?? undefined}
      />
    </>
  );
}

const mapConfig = {
  Locations: { metricKey: "locationsCount", tab: "locations" },
  Brands: { metricKey: "brandsCount", tab: "brands" },
  Assets: { metricKey: "assetsCount", tab: "assets" },
  Contacts: { metricKey: "contactsCount", tab: "contacts" },
};

const getClientTypeBadgeColors = (
  clientType: ClientColumnType["clientType"],
): { color: "indigo" | "cactus" } => {
  switch (clientType) {
    case "Primary":
      return { color: "indigo" };
    case "Secondary":
      return { color: "cactus" };
  }
};

export const mapClientApiToClient = (apiClient: ClientApiResponse): Client => ({
  id: apiClient.id,
  name: apiClient.clientName,
  entityName: apiClient.entityName ?? null,
  status: apiClient.status.label,
  clientType: apiClient.clientType.label,
  brands: apiClient.brandsCount,
  locations: apiClient.locationsCount,
  activeWorkOrders: apiClient.activeWorkOrdersCount,
  clientSuccessManager: apiClient.clientSuccessManager
    ? `${apiClient.clientSuccessManager.firstName} ${apiClient.clientSuccessManager.lastName}`
    : null,
  paymentMethod: apiClient.paymentMethod,
  paymentTerms: apiClient.paymentTerms?.label ?? null,
});

// Maps backend filterBy key → Zustand filter-store key (column label)
const CLIENTS_FILTER_KEY_TO_LABEL: Record<string, string> = {
  businessName: "Client Name",
  status: "Status",
  clientType: "Client Type",
  clientSuccessManager: "Client Success M",
  paymentTerms: "Payment Terms",
};

// Helper function to get sort field mapping
const getSortByField = (columnId: string): string | undefined => {
  const fieldMap: Record<string, string> = {
    name: "clientName",
    status: "status",
    clientType: "clientType",
    locations: "locationsCount",
    brands: "brandsCount",
    activeWorkOrders: "activeWorkOrdersCount",
    clientSuccessManager: "clientSuccessManager",
  };
  return fieldMap[columnId];
};
